import {
    useEffect,
    useMemo,
    useCallback,
    forwardRef,
    useImperativeHandle
} from "react";

import {
    useForm
} from "react-hook-form";

import {
    Box,
    Grid,
    FormControl,
    FormHelperText,
    TextField,
    Typography,
    InputAdornment,
} from "@mui/material";

import HomeIcon from "@mui/icons-material/Home";
import PersonIcon from "@mui/icons-material/Person";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import EmailIcon from "@mui/icons-material/Email";

import ModalTemplate from "../../../../common/modal/modalTemplate";
import {
    theme
} from "../../../../theme";
import {
    getSettings
} from "../../../../storage";

// 🔹 Reusable Form Field
const FormField = ({
    label,
    required,
    error,
    children,
    isAddStepFlowLabelMt
}) => {
    return (
        <FormControl
            error={!!error}
            fullWidth
            sx={{
                ...(isAddStepFlowLabelMt && {
                    mt: 2
                })
            }}
        >
            <Typography
                variant="body2"
                fontWeight="bold"
                mb={0.5}
            >
                {label}
                {required && (
                    <span className="required-asterisk">*</span>
                )}
            </Typography>

            {children}

            {error && (
                <FormHelperText>
                    {error.message}
                </FormHelperText>
            )}
        </FormControl>
    );
};

// 🔥 Edit Address Component
const EditAddress = forwardRef(({
    show = false,
    closeModal = () => { },
    jobGeolocationDetails,
    onUpdateAddress,
    onRequiredFieldsError, // 👈 validation error callback
    showModalView = true,
    showEditAddress = true,
    handleShowEditAddress = () => {}
}, ref) => {
    const settings = getSettings();
    const stepFlowIsEnable = settings.customer_web_job_step_flow_is_enable;

    const {
        register,
        handleSubmit,
        formState: {
            errors
        },
        reset,
    } = useForm({
        defaultValues: {
            company_or_customer: "",
            building_name_or_floor: "",
            street_name_and_number: "",
            neighborhood: "",
            postal_town: "",
            postcode: "",
        },
    });

    // =========================================================
    // Fields
    // =========================================================
    const fields = useMemo(() => [
        [
            {
                name: "company_or_customer",
                label: "Company/customer",
                icon: <PersonIcon />,
                isViewModal: false,
                isAddStepFlowLabelMt: false
            },
            {
                name: "building_name_or_floor",
                label: "Building/floor",
                icon: <HomeIcon />,
                isViewModal: false,
                isAddStepFlowLabelMt: false
            },
        ],
        [
            {
                name: "street_name_and_number",
                label: "Address line 1",
                required: true,
                rules: {
                    required: "Required"
                },
                icon: <HomeIcon />,
                isViewModal: true,
                isAddStepFlowLabelMt: false
            },
            {
                name: "neighborhood",
                label: "Address line 2",
                icon: <HomeIcon />,
                isViewModal: false,
                isAddStepFlowLabelMt: false
            },
        ],
        [
            {
                name: "postal_town",
                label: "Town / City",
                required: true,
                rules: {
                    required: "Required"
                },
                icon: <LocationCityIcon />,
                isViewModal: true,
                isAddStepFlowLabelMt: true
            },
            {
                name: "postcode",
                label: "Postcode",
                required: true,
                rules: {
                    required: "Required"
                },
                icon: <EmailIcon />,
                isViewModal: true,
                isAddStepFlowLabelMt: false
            },
        ],
    ], []);

    // =========================================================
    // Filter Fields
    // =========================================================
    const filteredFields = useMemo(() => {
        if (showModalView) {
            return fields;
        }

        return fields.map(row =>
            row.filter(field => field.isViewModal)
        ).filter(row => row.length > 0);
    }, [fields, showModalView]);

    // =========================================================
    // Reset Form When Address Changes
    // =========================================================
    useEffect(() => {
        if (!jobGeolocationDetails) {
            return;
        }

        reset({
            company_or_customer: jobGeolocationDetails.company_or_customer || "",
            building_name_or_floor: jobGeolocationDetails.building_name_or_floor || "",
            street_name_and_number: jobGeolocationDetails.street_name_and_number || "",
            neighborhood: jobGeolocationDetails.neighborhood || "",
            postal_town: jobGeolocationDetails.postal_town || "",
            postcode: jobGeolocationDetails.postcode || "",
        });

    }, [jobGeolocationDetails, reset]);

    // =========================================================
    // Valid Submit
    // =========================================================
    const onSubmit = useCallback((data) => {
        console.log("Address form submitted:", data);

        if (onUpdateAddress) {
            onUpdateAddress(data);
        } else {
            closeModal();
        }
    }, [onUpdateAddress, closeModal]);

    // =========================================================
    // Invalid Submit
    // =========================================================
    const onInvalid = useCallback((validationErrors) => {
        console.log("Address validation failed:", validationErrors);
        handleShowEditAddress();
        
        // 🔥 Call parent function when required fields are empty
        if (onRequiredFieldsError) {
            onRequiredFieldsError(
                validationErrors
            );
        }
    }, [onRequiredFieldsError, handleShowEditAddress]);

    // =========================================================
    // Expose Submit Function To Parent
    // =========================================================
    useImperativeHandle(ref, () => ({
        submitForm: async () => {
            return handleSubmit(
                onSubmit,
                onInvalid
            )();
        }
    }), [handleSubmit, onSubmit, onInvalid]);

    // =========================================================
    // Render Form
    // =========================================================
    const renderForm = () => (
        <div
            className={`card ${stepFlowIsEnable ? "job-card-bg-color-hash" : ""}`}
            style={{
                display: showEditAddress ? "" : "none"
            }}
        >
            <div className="card-body p-3">
                <Box
                    component="form"
                    onSubmit={handleSubmit(
                        onSubmit,
                        onInvalid
                    )}
                    noValidate
                >
                    <Box pt={1}>
                        {filteredFields.map((row, rowIndex) => (
                            <Grid
                                container
                                spacing={2}
                                columns={16}
                                key={rowIndex}
                            >
                                {row.map((field) => (
                                    <Grid
                                        item
                                        xs={showModalView ? 8 : 16}
                                        key={field.name}
                                    >
                                        <FormField
                                            label={field.label}
                                            required={field.required}
                                            error={errors[field.name]}
                                            isAddStepFlowLabelMt={
                                                field.isAddStepFlowLabelMt &&
                                                stepFlowIsEnable
                                            }
                                        >
                                            <TextField
                                                size="small"
                                                placeholder={field.label}
                                                error={!!errors[field.name]}
                                                {...register(
                                                    field.name,
                                                    field.rules
                                                )}
                                                fullWidth
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment
                                                            position="start"
                                                        >
                                                            <Box
                                                                sx={{
                                                                    color: theme.main_code,
                                                                    display: "flex",
                                                                    alignItems: "center"
                                                                }}
                                                            >
                                                                {field.icon}
                                                            </Box>
                                                        </InputAdornment>
                                                    ),
                                                    sx: {
                                                        ...(!showModalView && {height: 35}),
                                                        backgroundColor: "#ffffff",
                                                        borderRadius: "8px",
                                                    }
                                                }}
                                                sx={
                                                    showModalView === false ? {
                                                        "& .MuiInputBase-input": {
                                                            paddingTop: "10px",
                                                            paddingBottom: "8px",
                                                            fontSize: "14px",
                                                        }
                                                    } : undefined
                                                }
                                            />
                                        </FormField>
                                    </Grid>
                                ))}
                            </Grid>
                        ))}
                    </Box>

                    {/* =================================================
                        Save Button
                    ================================================= */}
                    {!stepFlowIsEnable && (
                        <div className="d-grid gap-2 mt-3">
                            <button
                                type="submit"
                                className={`btn btn-${theme.action_btn} btn-sm`}
                            >
                                Save
                            </button>
                        </div>
                    )}
                </Box>
            </div>
        </div>
    );

    // =========================================================
    // Modal View
    // =========================================================
    if (showModalView) {
        return (
            <ModalTemplate
                show={show}
                closeFun={closeModal}
                centered
                title="Edit address (if needed)"
                body={renderForm()}
                footer={null}
            />
        );
    }

    // =========================================================
    // Normal View
    // =========================================================
    return renderForm();
});

export default EditAddress;